import { useRef } from "react";
import {
  Box,
  Link,
  TableCell,
  TableRow,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import { ReactComponent as PartnerFusionSvg } from "assets/images/icons/partners_fusion.svg";
import { colors } from "theme";
import { DesignType, IDesign } from "types/design";

const useStyles = makeStyles((theme: Theme) => ({
  basicRow: {
    "&:hover": {
      backgroundColor: `${theme.palette.secondary.light} !important`,
    },
    borderRadius: 4,
    [theme.breakpoints.down(420)]: { display: "block" },
    "& .MuiLink-underlineNone.MuiTypography-colorSecondary:hover": {
      color: colors.violet[900],
    },
    "& > .MuiTableCell-sizeSmall": {
      [theme.breakpoints.up(420)]: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
      },
      [theme.breakpoints.down(420)]: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
      },
    },
    "& > .MuiTableCell-root": {
      fontSize: theme.typography.pxToRem(14),
      [theme.breakpoints.up(420)]: {
        borderTop: `1px solid ${colors.grey[200]}`,
        borderBottom: `1px solid ${colors.grey[200]}`,
      },
      [theme.breakpoints.down(420)]: {
        display: "block",
        width: "calc(100% - 34px)",
        borderRight: `1px solid ${colors.grey[200]}`,
        borderLeft: `1px solid ${colors.grey[200]}`,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        "&:not(:last-child)": { borderBottom: "none" },
      },
      "&:first-child": {
        borderLeft: `1px solid ${colors.grey[200]}`,
        borderTopLeftRadius: 4,
        [theme.breakpoints.up(420)]: { borderBottomLeftRadius: 4 },
        [theme.breakpoints.down(420)]: {
          borderTopRightRadius: 4,
          borderTop: `1px solid ${colors.grey[200]}`,
        },
      },
      "&:last-child": {
        borderRight: `1px solid ${colors.grey[200]}`,
        borderBottomRightRadius: 4,
        [theme.breakpoints.up(420)]: { width: 40, borderTopRightRadius: 4 },
        [theme.breakpoints.down(420)]: {
          borderBottomLeftRadius: 4,
          borderBottom: `1px solid ${colors.grey[200]}`,
        },
      },
    },
  },
  spacingRow: {
    height: 18,
    width: "100%",
  },
}));

const Row = ({ row }: { row: Partial<IDesign> }) => {
  const basicRow = useRef(null);
  const classes = useStyles();
  const isXXS = useMediaQuery((theme: Theme) => theme.breakpoints.down(420));

  return (
    <>
      <TableRow hover ref={basicRow} className={classes.basicRow}>
        <TableCell>
          <Link
            target="_blank"
            href={`/catalog/detail/${row.id}`}
            color="secondary"
          >
            <Typography variant="h6" component="span">
              {row.name}
            </Typography>
          </Link>
        </TableCell>
        <TableCell>
          {row.designType === DesignType.CATALOG_PANEL
            ? "Catalog Panel"
            : "Gene Module"}
        </TableCell>
        <TableCell>
          {row.readsRecommended}
          {isXXS ? <span> reads</span> : null}
        </TableCell>
        <TableCell>
          <Box display="flex" alignItems="center">
            {row.numberGenes}
            {isXXS ? <span> genes</span> : null}
            {row.coveredPartnerGenes!.length > 0 &&
            Object.keys(row.coveredPartnerTargets!).length > 0 ? (
              <Box marginLeft="auto" display="flex">
                <PartnerFusionSvg width={20} height={20} />
              </Box>
            ) : null}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow className={classes.spacingRow} role="presentation" />
    </>
  );
};

export default Row;
